import { gql } from '@apollo/client';
import debounce from 'lodash/debounce';
import { Validate } from 'react-hook-form';

import { apolloClient } from '@/client/startup/apollo-client';
import { EAttributeValidationRule } from '@/graphql-types/globalTypes';
import { REG_EXP } from '@/utilities';

import {
  AttributeValidationRules_IsOrganizationNameUnique,
  AttributeValidationRules_IsOrganizationNameUniqueVariables,
} from './graphql-types/AttributeValidationRules_IsOrganizationNameUnique';
import {
  AttributeValidationRules_IsUserEmailUnique,
  AttributeValidationRules_IsUserEmailUniqueVariables,
} from './graphql-types/AttributeValidationRules_IsUserEmailUnique';

const IS_USER_EMAIL_UNIQUE_QUERY = gql`
  query AttributeValidationRules_IsUserEmailUnique(
    $input: IsUserEmailUniqueInput!
  ) {
    isUserEmailUnique(input: $input) {
      isUnique
    }
  }
`;

const IS_ORGANIZATION_NAME_UNIQUE_QUERY = gql`
  query AttributeValidationRules_IsOrganizationNameUnique(
    $input: IsOrganizationNameUniqueInput!
  ) {
    isOrganizationNameUnique(input: $input) {
      isUnique
    }
  }
`;

type AttributeValidationRules = {
  email?: Validate<string | string[] | null>;
  required?: Validate<string | string[] | null>;
  uniqueUserEmail?: Validate<string | string[] | null>;
  uniqueOrganizationName?: Validate<string | string[] | null>;
};

export const attributeValidationRules = (options: {
  validationRules: EAttributeValidationRule[];
  label: string;
  initialValue: string | string[] | null;
  isRequired?: boolean;
}): AttributeValidationRules => {
  const { validationRules, label, initialValue, isRequired } = options;
  const validate: AttributeValidationRules = {};

  if (
    isRequired ||
    validationRules.includes(EAttributeValidationRule.REQUIRED)
  ) {
    validate.required = (value) => {
      if (typeof value === 'string' && value.length > 0) {
        return true;
      }

      if (Array.isArray(value) && value.length > 0) {
        return true;
      }

      return `${label} is required`;
    };
  }

  if (validationRules.includes(EAttributeValidationRule.EMAIL)) {
    validate.email = (value) => {
      // we only want to test if the current string is an email - if there is no string that is fine
      // if it should be required that would be handled by a different validation rule
      if (!value) {
        return true;
      }

      if (typeof value === 'string' && REG_EXP.EmailWithTLD.test(value)) {
        return true;
      }

      return 'You must enter a valid email address';
    };
  }

  if (validationRules.includes(EAttributeValidationRule.UNIQUE_USER_EMAIL)) {
    validate.uniqueUserEmail = debounce(
      async (value) => {
        if (!value) {
          return true;
        }
        if (typeof value !== 'string') {
          return 'User email must be a string';
        }

        if (
          typeof initialValue === 'string' &&
          value.toLowerCase() === initialValue.toLowerCase()
        ) {
          return true;
        }

        const { data } = await apolloClient.query<
          AttributeValidationRules_IsUserEmailUnique,
          AttributeValidationRules_IsUserEmailUniqueVariables
        >({
          query: IS_USER_EMAIL_UNIQUE_QUERY,
          variables: { input: { email: value } },
          fetchPolicy: 'no-cache',
        });

        if (data && data.isUserEmailUnique.isUnique) {
          return true;
        } else {
          return 'This email is already in use';
        }
      },
      500,
      { leading: true }
    );
  }

  if (
    validationRules.includes(EAttributeValidationRule.UNIQUE_ORGANIZATION_NAME)
  ) {
    validate.uniqueOrganizationName = debounce(
      async (value) => {
        if (!value) {
          return true;
        }
        if (typeof value !== 'string') {
          return 'Organization name must be a string';
        }

        if (
          typeof initialValue === 'string' &&
          value.toLowerCase() === initialValue.toLowerCase()
        ) {
          return true;
        }

        const { data } = await apolloClient.query<
          AttributeValidationRules_IsOrganizationNameUnique,
          AttributeValidationRules_IsOrganizationNameUniqueVariables
        >({
          query: IS_ORGANIZATION_NAME_UNIQUE_QUERY,
          variables: { input: { name: value } },
          fetchPolicy: 'no-cache',
        });

        if (data && data.isOrganizationNameUnique.isUnique) {
          return true;
        } else {
          return 'There is already an organization with this name';
        }
      },
      500,
      { leading: true }
    );
  }

  return validate;
};
